import { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store";
import "./Watchlist.scss";
import { SELECTOR_TYPE, updateSelectorType } from "../../reducers/period";
import {
  initImpersonationWatchlistItems,
  initWatchlistItems,
  resetWatchlistInitialized,
  setWatchlistItems,
} from "../../reducers/watchlist";
import { BonusInfoFooter } from "../../common/components/BonusInfoFooter/BonusInfoFooter";
import { ActionReportToolbar } from "../ActionReports/common/Toolbar/ActionReportToolbar";
import {
  ActionReportListView,
  ActionReportListViewLoader,
} from "../ActionReports/common/ActionReportListView/ActionReportListView";
import { KPI_ID } from "../../services/AboEnrichment/AboEnrichmentAPI.types";
import { IconSearchCriteria } from "../../common/icons/IconSearchCriteria/IconSearchCriteria";
import defaultContent from "../../content/watchlist";
import { getSavedKpiPreferencesForView } from "../../common/util/savedKpiPrefrencesUtil";
import { useGetWatchlistQuery } from "../../services/ActionReports/WatchlistBff";
import { useGetTreeViewQuery } from "../../services/TreeView/TreeViewAPI";
import { losParams } from "../TreeView/TreeViewDataLayer";
import { useAutoEnrichAll } from "../ActionReports/hooks/useAutoEnrichAll";
import { storedLocale } from "../Authentication/util";
import { padAff } from "../Authentication/impersonation-util";
import type { TreeNodeData } from "../../services/TreeView/TreeViewAPI.types";
import { MODULE_WATCHLIST } from "../../common/constants/export-constants";
import ServerSideExportInProgressNotify from "../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import ServerSideExportCompletedNotify from "../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import { dispatchOpenExportV2ModalEvent } from "../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { buildFieldTitleOverrides } from "../../common/util/exportUtil";
import type {
  SearchSelectItem,
  SearchCallback,
  SelectionCallback,
} from "../../common/components/SearchSelect/SearchSelect";
import type { AboKpiData } from "../../services/AboEnrichment/AboEnrichmentAPI.types";
import {
  watchlistPageViewTrack,
  watchlistCustomizeKpiTrack,
  watchlistCancelCustomizeKpiTrack,
  watchlistApplyCustomizeKpiTrack,
  watchlistSortAscendingTrack,
  watchlistSortDescendingTrack,
  watchlistExportTrack,
  watchlistProfileTrack,
  watchlistAddToWatchlistTrack,
  watchlistRemoveFromWatchlistTrack,
  watchlistPhoneTrack,
  watchlistMessageTrack,
} from "../../common/tealium/ActionReports/WatchlistTrack";
import { PageTitleSection } from "../../common/components/PageTitleSection/PageTitleSection";
import { getCountryNameFromCode } from "../../common/util/formatCountryName";
import maskAboNumber from "../../common/util/maskAboNumber";

// Default KPIs shown when user has no saved preferences
const DEFAULT_KPIS = ["groupPV", "ppvRequirement", "bonusPercent"];
// Default available KPIs when config is not defined
const DEFAULT_AVAILABLE_KPIS = [
  "groupPV",
  "ppvRequirement",
  "bonusPercent",
  "groupBV",
  "totalCustomerCount",
  "groupPVNeeded",
  "qualifiedLegs",
  "customerPV",
];
// Maximum number of KPIs that can be selected
const MAX_KPI_SELECTIONS = 8;

// Maps kpi keys to their KPI_ID values for enrichment API
const KPI_ID_MAP: Record<string, string> = {
  aboName: KPI_ID.ABO_NAME,
  groupPV: KPI_ID.GROUP_PV,
  ppvRequirement: KPI_ID.PERSONAL_PV_REQ_AR,
  personalPV: KPI_ID.PERSONAL_PV,
  personalPVRequirement: KPI_ID.PERSONAL_PV_REQ_AR,
  bonusPercent: KPI_ID.BONUS_PERCENT,
  groupBV: KPI_ID.GROUP_BV,
  totalCustomerCount: KPI_ID.CUSTOMER_COUNT,
  groupPVNeeded: KPI_ID.GROUP_PV_NEEDED,
  qualifiedLegs: KPI_ID.QUALIFIED_LEGS,
  customerPV: KPI_ID.CUSTOMER_PV,
  rubyPV: KPI_ID.RUBY_PV,
  totalABOCount: KPI_ID.GROUP_SIZE,
  personalOrdersCount: KPI_ID.PERSONAL_ORDERS_COUNT,
  groupOrdersCount: KPI_ID.GROUP_ORDERS_COUNT,
  totalNewABOCount: KPI_ID.SPONSORING,
  annualPPV: KPI_ID.ANNUAL_PPV,
  vcsPV: KPI_ID.VCS_PV,
  nonVCSPV: KPI_ID.NON_VCS_PV,
  personalVcsPercent: KPI_ID.VCS_PERCENT,
  isFirstContractYear: KPI_ID.IS_FIRST_CONTRACT_YEAR,
};

const createFieldConfig = (tableColumns: Record<string, string>, availableKpis: string[]) =>
  availableKpis
    .filter((kpi) => KPI_ID_MAP[kpi])
    .map((kpi) => ({
      id: KPI_ID_MAP[kpi],
      kpi,
      title: tableColumns[kpi] || kpi,
    }));

const buildLevelMap = (node: TreeNodeData | null | undefined): Map<string, number> => {
  const map = new Map<string, number>();
  if (!node) return map;

  const recurse = (n: TreeNodeData) => {
    map.set(`${n.aff}-${n.abo}`, n.level);
    if (n.downlines) {
      n.downlines.forEach(recurse);
    }
  };
  recurse(node);
  return map;
};

const useSelectedKpis = (availableKpis: string[]) => {
  const { savedKpiPreferencesData } = useAppSelector((state) => state.savedKpiPreferencesData);

  return useMemo(() => {
    const savedKpis = savedKpiPreferencesData
      ? getSavedKpiPreferencesForView("watchlistFilters", savedKpiPreferencesData)
      : [];

    if (savedKpis.length > 0) {
      const filtered = savedKpis.filter((kpi) => availableKpis.includes(kpi));
      if (filtered.length > 0) return filtered;
    }

    return DEFAULT_KPIS.filter((kpi) => availableKpis.includes(kpi));
  }, [savedKpiPreferencesData, availableKpis]);
};

export default function Watchlist() {
  const { t } = useTranslation(["watchlist", "common"]);
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState("");
  const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [sortSnapshot, setSortSnapshot] = useState<Map<string, number | null>>(new Map());
  const { configuration, user, isImpersonation } = useAppSelector((state) => state.boot);
  const availableKpis = configuration.uiKpis?.watchlistV2 ?? DEFAULT_AVAILABLE_KPIS;
  // isFirstContractYear is used for enrichment and export but should not appear as a selectable column
  const displayKpis = useMemo(() => availableKpis.filter((kpi) => kpi !== "isFirstContractYear"), [availableKpis]);
  const selectedKpis = useSelectedKpis(displayKpis);

  const { initialItems, impersonationItems } = useAppSelector((state) => state.watchlist);
  const isImpersonationSession = isImpersonation && configuration.impersonation?.disableWatchlistMutations === true;
  const displayItems = isImpersonationSession ? impersonationItems : initialItems;
  const { selectedPeriod } = useAppSelector((state) => state.period);

  const isMaskConfidentialAboNumber =
    useAppSelector((s) => s.boot.configuration.actionReports.maskConfidentialAboNumber) ?? false;
  const { data: treeData } = useGetTreeViewQuery({
    Aff: user.aff,
    Abo: user.abo,
    Period: selectedPeriod,
    IsoCountryCode: user.isoCountryCode,
    Locale: storedLocale(),
    ...losParams,
  });

  const levelMap = useMemo(() => buildLevelMap(treeData?.treeData), [treeData]);

  // Track whether levels have been applied to avoid re-running enrichment
  const [appliedLevelMap, setAppliedLevelMap] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    if (levelMap.size > 0 && appliedLevelMap.size === 0) {
      setAppliedLevelMap(levelMap);
    }
  }, [levelMap, appliedLevelMap.size]);

  // The period-aware endpoint filters to active downline ABOs server-side (matching v1 behavior)
  const {
    data: bffData,
    isLoading: isBFFLoading,
    isFetching: isBFFFetching,
    refetch: refetchWatchlist,
  } = useGetWatchlistQuery(
    { ownerAff: user.aff, ownerAbo: user.abo, period: selectedPeriod },
    { refetchOnMountOrArgChange: true },
  );

  const isLoading = isBFFLoading || isBFFFetching;

  // Populate Redux with filtered watchlist data from BFF
  const paddedBFFData = useMemo(() => {
    if (!bffData?.watchList) return undefined;
    return bffData.watchList
      .filter((abo) => abo.Aff !== null && abo.ABONo !== null)
      .map((abo) => ({
        ...abo,
        Aff: padAff(abo.Aff),
      }));
  }, [bffData]);

  useEffect(() => {
    if (isImpersonationSession) {
      if (paddedBFFData) dispatch(initImpersonationWatchlistItems(paddedBFFData));
      return;
    }

    if (isBFFFetching) {
      dispatch(setWatchlistItems([]));
    } else if (paddedBFFData) {
      dispatch(initWatchlistItems(paddedBFFData));
      dispatch(setWatchlistItems(paddedBFFData));
    }
  }, [paddedBFFData, isBFFFetching, isImpersonationSession, dispatch]);
  const enrichedAbosRaw = useAppSelector((state) => state.aboEnrichment["watchlist"]?.[selectedPeriod]?.enrichedData);
  const enrichedAbos: AboKpiData[] = useMemo(() => enrichedAbosRaw ?? [], [enrichedAbosRaw]);

  const reportLength = displayItems.length;

  const textOverrides = displayKpis.reduce(
    (acc, key) => {
      acc[key] = `${t(`table.columns.${key}`, defaultContent.table.columns[key] || key)}`;
      return acc;
    },
    {} as { [key: string]: string },
  );

  const tableColumns = t("table.columns", {
    ns: "watchlist",
    returnObjects: true,
    defaultValue: defaultContent.table.columns,
  }) as Record<string, string>;

  const fieldConfig = useMemo(() => {
    const config = createFieldConfig(tableColumns, availableKpis);
    // Always include aboName so the enrichment API resolves locale-aware names
    if (!config.some((f) => f.id === KPI_ID.ABO_NAME)) {
      config.unshift({ id: KPI_ID.ABO_NAME, kpi: "aboName", title: "" });
    }
    // Include phone number and image ref URL for profile section and contact icons
    if (!config.some((f) => f.id === KPI_ID.PHONE_NUMBER)) {
      config.push({ id: KPI_ID.PHONE_NUMBER, kpi: "phoneNumber", title: "" });
    }
    if (!config.some((f) => f.id === KPI_ID.IMAGE_REF_URL)) {
      config.push({ id: KPI_ID.IMAGE_REF_URL, kpi: "imageRefURL", title: "" });
    }
    if (!config.some((f) => f.id === KPI_ID.WHATSAPP)) {
      config.push({ id: KPI_ID.WHATSAPP, kpi: "whatsapp", title: "" });
    }
    return config;
  }, [tableColumns, availableKpis]);

  const reportData = useMemo(() => {
    const data = displayItems.map((record) => ({
      aboName: "",
      aboNumber: record.ABONo,
      abo: record.ABONo,
      aff: record.Aff,
      isoCountry: record.IsoCountry ?? "",
      level: appliedLevelMap.get(`${Number(record.Aff)}-${Number(record.ABONo)}`),
      isConfidential: record?.PrivacyFlag ?? false,
    }));

    if (sortSnapshot.size === 0) return data;

    return [...data].sort((rowA, rowB) => {
      const valueA = sortSnapshot.get(`${rowA.aff}-${rowA.abo}`) ?? null;
      const valueB = sortSnapshot.get(`${rowB.aff}-${rowB.abo}`) ?? null;

      if (valueA === null && valueB === null) return 0;
      if (valueA === null) return 1;
      if (valueB === null) return -1;

      return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
    });
  }, [displayItems, sortDirection, sortSnapshot, appliedLevelMap]);

  const { isAllEnriched } = useAutoEnrichAll({
    reportId: "watchlist",
    period: selectedPeriod,
    reportData,
    fieldConfig,
    locale: storedLocale(),
    isoCountryCode: user.isoCountryCode || "US",
  });

  useEffect(() => {
    dispatch(resetWatchlistInitialized());
    dispatch(updateSelectorType(SELECTOR_TYPE.MONTHLY));
    return () => {
      dispatch(resetWatchlistInitialized());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isImpersonationSession) dispatch(resetWatchlistInitialized());

    refetchWatchlist();
    // refetchWatchlist is supplied by RTK Query and is intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, isImpersonationSession, dispatch]);

  useEffect(() => {
    watchlistPageViewTrack(selectedPeriod);
  }, [selectedPeriod]);

  const handleSortClick = () => {
    const snapshot = new Map<string, number | null>();
    for (const abo of enrichedAbos) {
      snapshot.set(`${abo.aff}-${abo.abo}`, abo.bonusPercent?.value ?? null);
    }
    setSortSnapshot(snapshot);
    if (sortDirection === "asc") {
      watchlistSortDescendingTrack();
    } else {
      watchlistSortAscendingTrack();
    }
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const exportAffAbos = useMemo(
    () =>
      reportData.map((record) => ({
        affAbo: `${record.aff}-${record.abo}`,
      })),
    [reportData],
  );

  const startedByLabels = useMemo<Record<string, string>>(
    () => ({
      [MODULE_WATCHLIST]: t("title.pageTitle", defaultContent.title.pageTitle).toString(),
    }),
    [t],
  );

  const exportSelectedKpis = useMemo(() => {
    if (availableKpis.includes("isFirstContractYear")) {
      return [...selectedKpis, "isFirstContractYear"];
    }
    return selectedKpis;
  }, [selectedKpis, availableKpis]);

  const fieldTitleOverrides = useMemo(() => buildFieldTitleOverrides(tableColumns), [tableColumns]);

  const getEnrichedName = useCallback(
    (aff: string, abo: string): string => {
      const enriched = enrichedAbos.find((e) => e.aff === aff && e.abo === abo);
      return enriched?.aboName ?? "";
    },
    [enrichedAbos],
  );

  const handleSearchCallback = useCallback<SearchCallback>(
    (text) =>
      reportData
        .filter((entry) => {
          const name = getEnrichedName(entry.aff, entry.abo);
          return name.toLowerCase().includes(text.toLowerCase()) || entry.abo.includes(text);
        })
        .map((entry): SearchSelectItem => {
          const sublabel = `${isMaskConfidentialAboNumber && entry.isConfidential ? maskAboNumber(entry.abo) : entry.abo} | ${getCountryNameFromCode(entry.isoCountry, storedLocale())}`;

          return {
            key: `${entry.aff}-${entry.abo}`,
            label: getEnrichedName(entry.aff, entry.abo),
            sublabel,
          };
        }),
    [reportData, getEnrichedName, isMaskConfidentialAboNumber],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const index = reportData.findIndex((entry) => `${entry.aff}-${entry.abo}` === key);
      const found = reportData[index];
      if (found) {
        const name = getEnrichedName(found.aff, found.abo);
        setScrollToIndex(index);
        setSearchText(name);
      }
      const displayName = found ? getEnrichedName(found.aff, found.abo) : "";
      return { displayName };
    },
    [reportData, getEnrichedName],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToIndex(undefined);
  }, []);

  const handleWatchlistTrack = useCallback((isAdding: boolean) => {
    if (isAdding) {
      watchlistAddToWatchlistTrack();
    } else {
      watchlistRemoveFromWatchlistTrack();
    }
  }, []);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="Watchlist__loading">
          <ActionReportListViewLoader />
        </div>
      );
    }

    if (reportLength > 0) {
      return (
        <ActionReportListView
          selectedKpis={selectedKpis}
          reportId="watchlist"
          reportData={reportData}
          fieldConfig={fieldConfig}
          enableWatchlist={true}
          skipEnrichment={true}
          awaitAutoEnrichment={!isAllEnriched}
          scrollToIndex={scrollToIndex}
          showImageBlock={true}
          onProfileTrack={watchlistProfileTrack}
          onWatchlistTrack={handleWatchlistTrack}
          onPhoneTrack={watchlistPhoneTrack}
          onMessageTrack={watchlistMessageTrack}
        />
      );
    }

    return (
      <div className="Watchlist__noDownlines">
        <main className="error">
          <div className="error__icon">
            <IconSearchCriteria />
          </div>
          <div className="error__message">{`${t("noDownlines", defaultContent.noDownlines)}`}</div>
        </main>
      </div>
    );
  };

  return (
    <main className="Watchlist">
      <PageTitleSection title={`${t("title.pageTitle", defaultContent.title.pageTitle)}`} hideTimeStamp={true} />
      <ActionReportToolbar
        sort={{
          enabled: false,
          direction: sortDirection,
          onClick: handleSortClick,
          disabled: reportLength === 0,
        }}
        kpiSelector={{
          availableKpis: displayKpis,
          defaultKpis: selectedKpis,
          saveViewName: "watchlistFilters",
          saveViewDescription: "Watchlist Filter Preferences",
          maxSelectionsAllowed: MAX_KPI_SELECTIONS,
          textOverrides,
          onOpenTrack: watchlistCustomizeKpiTrack,
          onCancelTrack: watchlistCancelCustomizeKpiTrack,
          onApplyTrack: watchlistApplyCustomizeKpiTrack,
          disabled: reportLength === 0,
        }}
        search={{
          enabled: true,
          searchCallback: handleSearchCallback,
          selectionCallback: handleSelectionCallback,
          searchText,
          clearFunction: handleClearSearch,
          disabled: reportLength === 0,
        }}
        export={{
          moduleId: MODULE_WATCHLIST,
          exportAffAbos: exportAffAbos,
          selectedKpis: exportSelectedKpis,
          confirmationMessage: t("export.description", defaultContent.export.description).toString(),
          exportType: MODULE_WATCHLIST,
          startedByLabels,
          fieldTitleOverrides,
        }}
        exportTrack={watchlistExportTrack}
        reportLength={reportLength}
        isAllEnriched={isAllEnriched}
      />
      <div className="Watchlist__content">{renderContent()}</div>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify
        moduleId={MODULE_WATCHLIST}
        notifyDownloadLinkOnClick={() => dispatchOpenExportV2ModalEvent(MODULE_WATCHLIST, true)}
      />
      <BonusInfoFooter />
    </main>
  );
}
